import React, { useCallback, useEffect, useState, useRef, Children, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
interface CaseStudyPageProps {
  onBack: () => void;
  onNext?: () => void;
  nextLabel?: string;
}
const sections = [{
  id: 'challenge',
  label: 'The Challenge'
}, {
  id: 'impact',
  label: 'Impact'
}, {
  id: 'understanding',
  label: 'Divergences'
}, {
  id: 'solution',
  label: 'The Solution'
}, {
  id: 'component-system',
  label: 'System Elements'
}, {
  id: 'design-principles',
  label: 'Design Principles'
}, {
  id: 'building-for-scale',
  label: 'Building for Scale'
}, {
  id: 'consistency',
  label: 'Brand Theming'
}, {
  id: 'measuring-success',
  label: 'Measuring Success'
}, {
  id: 'key-learnings',
  label: 'Key Learnings'
}];
export function CaseStudyPage({
  onBack,
  onNext,
  nextLabel = 'Next Project'
}: CaseStudyPageProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [impactSlide, setImpactSlide] = useState(0);
  const isScrollingRef = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.round(scrollTop / docHeight * 100) : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Track which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (isScrollingRef.current) return;
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = sections.findIndex(s => s.id === entry.target.id);
          if (idx !== -1) setActiveIndex(idx);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, sections.length - 1));
    const el = document.getElementById(sections[clamped].id);
    if (el) {
      isScrollingRef.current = true;
      setActiveIndex(clamped);
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  }, []);
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <div className="min-h-screen bg-[#F8F6F3] text-[#6B6560] selection:bg-[#1A1A1A]/10 selection:text-[#1A1A1A]" data-id="element-328">
      <div className="max-w-5xl mx-auto px-8 md:px-16 py-12 md:py-20 pb-32" data-id="element-329">
        {/* Back Navigation */}
        <motion.button onClick={() => {
        onBack();
        setTimeout(() => {
          document.getElementById('work')?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      }} initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="group flex items-center gap-2 text-sm font-medium text-[#6B6560] hover:text-[#1A1A1A] mb-12 transition-colors" data-id="element-330">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" data-id="element-331" />
          Back to Work
        </motion.button>

        {/* Hero Section */}
        <motion.section initial="hidden" animate="visible" variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-332">
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 leading-tight" data-id="element-333">
            Creating a design system to serve millions of Canadians
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl md:text-2xl text-[#6B6560] max-w-3xl leading-relaxed mb-12" data-id="element-334">
            Using tokens to power a multi-brand design system serving Loblaw
            Digital's portfolio of retail brands.
          </motion.p>

          <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[#E8E4DF] pt-8 mb-16" data-id="element-335">
            <div data-id="element-336">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-337">
                Timeframe
              </h3>
              <p className="text-[#1A1A1A]" data-id="element-338">2024-current</p>
            </div>
            <div data-id="element-339">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-340">
                Roles
              </h3>
              <div className="space-y-1" data-id="element-341">
                <p className="text-[#1A1A1A]" data-id="element-342">Component designer</p>
                <p className="text-[#1A1A1A]" data-id="element-343">Token designer</p>
                <p className="text-[#1A1A1A]" data-id="element-344">Content modeler</p>
                <p className="text-[#1A1A1A]" data-id="element-345">Adoption champion</p>
              </div>
            </div>
            <div data-id="element-346">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-347">
                Tools
              </h3>
              <div className="space-y-1" data-id="element-348">
                <p className="text-[#1A1A1A]" data-id="element-349">Figma</p>
                <p className="text-[#1A1A1A]" data-id="element-350">Tokens Studio</p>
                <p className="text-[#1A1A1A]" data-id="element-351">Miro</p>
                <p className="text-[#1A1A1A]" data-id="element-352">Coda</p>
                <p className="text-[#1A1A1A]" data-id="element-353">Storybook</p>
                <p className="text-[#1A1A1A]" data-id="element-354">Contentful</p>
              </div>
            </div>
            <div data-id="element-355">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-356">
                Design team size
              </h3>
              <p className="text-[#1A1A1A]" data-id="element-357">4</p>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full aspect-video bg-[#E8E4DF] rounded-2xl relative overflow-hidden" data-id="element-358">
            <video src="https://cdn.magicpatterns.com/uploads/9s9roW7JuU8sDCRuW91ytA/helios sizzle.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-359" />
          </motion.div>
        </motion.section>

        {/* The Challenge */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32 scroll-mt-8" data-id="element-360">
          <motion.h2 id="challenge" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-361">
            Challenge
          </motion.h2>
          <motion.div variants={fadeInUp} className="max-w-3xl" data-id="element-362">
            <p className="text-lg leading-relaxed" data-id="element-363">
              Over the years, Loblaw Digital's component libraries became
              cumbersome to maintain and use. Components with a similar purpose
              would look completely different. Styles were inconsistent
              throughout experiences. Additionally, each brand (Shoppers Drug
              Mart, PC Express, PC Optimum &amp; Joe Fresh) had their own
              libraries which further compounded toil and confusion for
              designers and developers alike.
            </p>
          </motion.div>
        </motion.section>

        {/* Solution */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-364">
          <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6" data-id="element-365">
            Solution
          </motion.h2>
          <motion.div variants={fadeInUp} className="max-w-3xl" data-id="element-366">
            <p className="text-lg leading-relaxed" data-id="element-367">
              A multi-brand, headless design system powered by tokens that can
              be used across all brands on sites and apps.
            </p>
          </motion.div>
        </motion.section>

        {/* Results */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-368">
          <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6" data-id="element-369">
            Results
          </motion.h2>
          <motion.div variants={fadeInUp} className="max-w-3xl" data-id="element-370">
            <p className="text-lg leading-relaxed" data-id="element-371">
              Successfully scaled the design system to be used by all 14 brands
              within the Loblaw Digital ecosystem. A feat so unique that{' '}
              <a href="https://www.figma.com/customers/how-loblaw-digital-built-a-federated-design-system-for-14-brands-with-figma/" target="_blank" rel="noopener noreferrer" className="text-[#6BAFCB] underline hover:text-[#5A9AB8] transition-colors" data-id="element-372">
                Figma featured our story
              </a>
              .
            </p>
          </motion.div>
        </motion.section>

        {/* Impact */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-373">
          <motion.h2 id="impact" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-374">
            Impact
          </motion.h2>

          {/* Desktop: Arrow carousel with arrows in margins */}
          <motion.div variants={fadeInUp} className="hidden md:flex items-center -mx-14" data-id="element-375">
            <button onClick={() => setImpactSlide(prev => (prev - 1 + 3) % 3)} className="w-10 h-10 rounded-full bg-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#D8D3CD] transition-all shrink-0 hover:-translate-x-[4px]" aria-label="Previous slide" data-id="element-376">
              <ChevronLeft size={18} data-id="element-377" />
            </button>

            <div className="flex-1 overflow-hidden mx-4" data-id="element-378">
              <AnimatePresence mode="wait" data-id="element-379">
                {impactSlide === 0 && <motion.div key="web" initial={{
                opacity: 0,
                x: 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -40
              }} transition={{
                duration: 0.35,
                ease: 'easeOut'
              }} className="bg-white rounded-2xl p-10 md:p-12" data-id="element-380">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-8" data-id="element-381">
                      Web
                    </h3>
                    <div className="grid grid-cols-3 gap-8" data-id="element-382">
                      <div data-id="element-383">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-384">
                          56
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-385">
                          Teams using web components
                        </span>
                      </div>
                      <div data-id="element-386">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-387">
                          465
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-388">
                          Web components built
                        </span>
                      </div>
                      <div data-id="element-389">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-390">
                          30k+
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-391">
                          Weekly inserts
                        </span>
                      </div>
                    </div>
                  </motion.div>}

                {impactSlide === 1 && <motion.div key="ios" initial={{
                opacity: 0,
                x: 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -40
              }} transition={{
                duration: 0.35,
                ease: 'easeOut'
              }} className="bg-white rounded-2xl p-10 md:p-12" data-id="element-392">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-8" data-id="element-393">
                      iOS
                    </h3>
                    <div className="grid grid-cols-3 gap-8" data-id="element-394">
                      <div data-id="element-395">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-396">
                          49
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-397">
                          Teams using components
                        </span>
                      </div>
                      <div data-id="element-398">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-399">
                          359
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-400">
                          Components built
                        </span>
                      </div>
                      <div data-id="element-401">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-402">
                          13k+
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-403">
                          Weekly inserts
                        </span>
                      </div>
                    </div>
                  </motion.div>}

                {impactSlide === 2 && <motion.div key="android" initial={{
                opacity: 0,
                x: 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -40
              }} transition={{
                duration: 0.35,
                ease: 'easeOut'
              }} className="bg-white rounded-2xl p-10 md:p-12" data-id="element-404">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-8" data-id="element-405">
                      Android
                    </h3>
                    <div className="grid grid-cols-3 gap-8" data-id="element-406">
                      <div data-id="element-407">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-408">
                          42
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-409">
                          Teams using components
                        </span>
                      </div>
                      <div data-id="element-410">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-411">
                          283
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-412">
                          Components built
                        </span>
                      </div>
                      <div data-id="element-413">
                        <span className="text-5xl md:text-6xl font-bold text-[#1A1A1A] block mb-2" data-id="element-414">
                          150+
                        </span>
                        <span className="text-sm text-[#6B6560]" data-id="element-415">
                          Weekly inserts
                        </span>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button onClick={() => setImpactSlide(prev => (prev + 1) % 3)} className="w-10 h-10 rounded-full bg-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#D8D3CD] transition-all shrink-0 hover:translate-x-[4px]" aria-label="Next slide" data-id="element-416">
              <ChevronRight size={18} data-id="element-417" />
            </button>
          </motion.div>

          {/* Mobile: Horizontal scroll with peek */}
          <motion.div variants={fadeInUp} className="md:hidden -mx-8 px-8 overflow-x-auto snap-x snap-mandatory flex gap-4 scrollbar-hide" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-418">
            <div className="bg-white rounded-2xl p-8 snap-start shrink-0 w-[85vw]" data-id="element-419">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-6" data-id="element-420">Web</h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-421">
                <div data-id="element-422">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-423">
                    56
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-424">
                    Teams using web components
                  </span>
                </div>
                <div data-id="element-425">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-426">
                    465
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-427">
                    Web components built
                  </span>
                </div>
                <div data-id="element-428">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-429">
                    30k+
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-430">Weekly inserts</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 snap-start shrink-0 w-[85vw]" data-id="element-431">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-6" data-id="element-432">iOS</h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-433">
                <div data-id="element-434">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-435">
                    49
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-436">
                    Teams using components
                  </span>
                </div>
                <div data-id="element-437">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-438">
                    359
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-439">
                    Components built
                  </span>
                </div>
                <div data-id="element-440">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-441">
                    13k+
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-442">Weekly inserts</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 snap-start shrink-0 w-[85vw]" data-id="element-443">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-6" data-id="element-444">Android</h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-445">
                <div data-id="element-446">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-447">
                    42
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-448">
                    Teams using components
                  </span>
                </div>
                <div data-id="element-449">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-450">
                    283
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-451">
                    Components built
                  </span>
                </div>
                <div data-id="element-452">
                  <span className="text-3xl font-bold text-[#1A1A1A] block mb-1" data-id="element-453">
                    150+
                  </span>
                  <span className="text-xs text-[#6B6560]" data-id="element-454">Weekly inserts</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* The Problem */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-455">
          <motion.h2 id="understanding" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-456">
            Divergences
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl" data-id="element-457">
            We conducted a comprehensive audit of the digital properties across
            all banners. The results were clear: we were solving the same
            problems over and over again. A simple button component was
            implemented 14 different ways across the organization, each with
            different bugs and accessibility flaws.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16" data-id="element-458">
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-459">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" data-id="element-460">
                User Pain Points
              </h3>
              <ul className="space-y-4" data-id="element-461">
                {['Inconsistent accessibility across brands', 'Duplicated effort for basic UI components', 'Design drift between Figma and production', 'Slow onboarding for new designers'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-462">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" data-id="element-463" />
                    <span data-id="element-464">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-465">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" data-id="element-466">
                Business Challenges
              </h3>
              <ul className="space-y-4" data-id="element-467">
                {['High cost of maintaining multiple codebases', 'Slow rollout of enterprise-wide features', 'Fragmented brand experience', 'Difficulty scaling to new verticals'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-468">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" data-id="element-469" />
                    <span data-id="element-470">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} className="pl-6 md:pl-8 border-l-4 border-[#1A1A1A] mb-16" data-id="element-471">
            <p className="text-xl md:text-2xl text-[#6B6560] leading-relaxed mb-8 italic" data-id="element-472">
              "I also appreciate how thoroughly organized JP is. Especially in a
              challenging, sometimes chaotic environment, the attention that he
              gives to this really keeps things on track. It has huge benefits
              for the team."
            </p>
            <div data-id="element-473">
              <p className="text-[#1A1A1A] font-medium text-lg" data-id="element-474">
                James Harrison
              </p>
              <p className="text-[#6B6560] text-lg" data-id="element-475">
                Staff Product Designer, Platform
              </p>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden" data-id="element-476">
            <img src="https://cdn.magicpatterns.com/uploads/fc9vrVn6UxnadSbUFm8dSv/Screenshot_2025-11-17_at_9.55.22_PM.png" alt="Miro audit workshop highlighting divergences between brands" className="w-full h-auto block rounded-2xl" data-id="element-477" />
            <p className="mt-3 text-xs text-[#A09A94] text-center leading-relaxed" data-id="element-478">
              One of multiple Miro audit workshops I ran highlighting the
              divergences between brands for the same components
            </p>
          </motion.div>
        </motion.section>

        {/* The Solution */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-479">
          <motion.h2 id="solution" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-480">
            The Solution: Helios
          </motion.h2>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start" data-id="element-481">
            {/* Left: Text */}
            <div data-id="element-482">
              <p className="text-xl md:text-2xl text-[#6B6560] leading-relaxed" data-id="element-483">
                We built Helios: a multi-brand design system powered by design
                tokens. By separating semantic structure from visual styling, we
                created a library of 'headless' components that can be themed
                instantly. This allows a single button component to look like a
                Loblaws button in one context and a Joe Fresh button in another,
                while sharing the same robust logic and accessibility features.
              </p>
            </div>

            {/* Right: Image */}
            <div data-id="element-484">
              <img src="https://cdn.magicpatterns.com/uploads/eaZ6K51EHN2RsjVpaA4Qjd/Tokens_Architecture.png" alt="Helios token architecture diagram showing Figma, Web Core, iOS Core, Android Core, Design Tokens, and brand examples" className="w-full h-auto block rounded-2xl" data-id="element-485" />
            </div>
          </motion.div>
        </motion.section>

        {/* Helios Screens */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-486">
          <motion.div variants={fadeInUp} data-id="element-487">
            <img src="https://cdn.magicpatterns.com/uploads/4md3TeHXs8Jy4YKp8WoVdj/screens.png" alt="Helios design system in action across PC Express and Shoppers Drug Mart" className="w-full h-auto block rounded-2xl" data-id="element-488" />
          </motion.div>
        </motion.section>

        {/* Component Build */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-489">
          <motion.h2 id="component-system" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-490">
            System Elements
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-491">
            {[{
            title: 'Design Tokens',
            desc: 'The atomic foundation: semantic variables for color, typography, spacing, and motion.',
            panImage: 'https://cdn.magicpatterns.com/uploads/vUwvm4h5TkN5YtxAwfCczw/Tokens.png',
            panDirection: 'horizontal' as const
          }, {
            title: 'Core Components',
            desc: 'A library of 40+ accessible, functional atoms, molecules and organisms.',
            panImage: 'https://cdn.magicpatterns.com/uploads/uiMgXBhE6UEmJiAXkYK4F6/Core_components.png',
            panDirection: 'horizontal' as const
          }, {
            title: 'Content Models',
            desc: 'Almost all components were powered by their respective content model.',
            panImage: 'https://cdn.magicpatterns.com/uploads/wZqoHSuWgQASrq1UY44ssW/Content_models.png',
            panDirection: 'horizontal' as const
          }, {
            title: 'Documentation',
            desc: 'A comprehensive documentation site (we used Storybook) to act as single source of truth for developers and content authors.',
            panImage: 'https://cdn.magicpatterns.com/uploads/izhzhDW7o5Y1KphCZw381C/Documentation.png',
            panDirection: 'horizontal' as const
          }, {
            title: 'Figma Plugins',
            desc: 'Using Tokens Studio and our custom gitlab integration, designers were able to push tokens directly to our code base. Making design changes effortless for developers.',
            panImage: 'https://cdn.magicpatterns.com/uploads/oD61HL6uzMKZuKeotwfYnw/Figma_Plugins.png',
            panDirection: 'vertical' as const
          }, {
            title: 'Governance',
            desc: 'A contribution model that empowers teams to evolve the system.',
            panImage: 'https://cdn.magicpatterns.com/uploads/a8kKWNAVpj1ZKJqdrbR5d6/Governance.png',
            panDirection: 'horizontal' as const
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="group bg-white rounded-2xl border border-[#E8E4DF] overflow-hidden" data-id="element-492">
                {card.panImage ? <div className="h-32 w-full" style={{
              backgroundImage: `url(${card.panImage})`,
              backgroundSize: card.panDirection === 'vertical' ? '100% auto' : 'auto 100%',
              backgroundPosition: '0% 0%',
              backgroundRepeat: 'no-repeat',
              transition: 'background-position 3s ease-in-out'
            }} onMouseEnter={e => {
              if (card.panDirection !== 'vertical') {
                e.currentTarget.style.backgroundPosition = '100% 0%';
              }
            }} onMouseLeave={e => {
              if (card.panDirection !== 'vertical') {
                e.currentTarget.style.backgroundPosition = '0% 0%';
              }
            }} data-id="element-493" /> : <div className="h-32 bg-[#E8E4DF] w-full" data-id="element-494" />}
                <div className="p-6" data-id="element-495">
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-2" data-id="element-496">
                    {card.title}
                  </h3>
                  <p className="text-sm" data-id="element-497">{card.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Video Section */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-498">
          <motion.div variants={fadeInUp} className="w-full aspect-video bg-[#E8E4DF] rounded-2xl relative overflow-hidden" data-id="element-499">
            <video src="https://cdn.magicpatterns.com/uploads/axziFiagMHZPYLZ8X9Fbwg/TUNI1dPYjj94Sl5Ta5PPEKRZRyU.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-500" />
          </motion.div>
        </motion.section>

        {/* Design Principles */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-501">
          <motion.h2 id="design-principles" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-502">
            Design Principles
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-503">
            {[{
            title: 'Inclusive by Default',
            desc: "Accessibility isn't an afterthought. Every component is built to WCAG AA standards from day one."
          }, {
            title: 'Brand Distinctiveness',
            desc: "The system must support radical visual differences. We don't just swap colors; we change typography, shapes, and density."
          }, {
            title: 'Developer Experience',
            desc: "If it's hard to use, it won't be used. We prioritize intuitive APIs, strong typing, and great docs."
          }, {
            title: 'Systematic Evolution',
            desc: 'The system is a living product. We iterate based on usage data and feedback from product teams.'
          }].map((principle, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-504">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-505">
                  {principle.title}
                </h3>
                <p className="leading-relaxed" data-id="element-506">{principle.desc}</p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Building for Scale */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-507">
          <motion.h2 id="building-for-scale" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-508">
            Building for Scale
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl" data-id="element-509">
            To serve millions of Canadians across diverse brands, we had to
            think beyond a single UI kit. The components in our library were
            served to the front end with Server Driven UI. SDUI is a pattern
            where the server decides what the UI looks like i.e. what components
            to render, in what order, with what configuration. This technical
            nuance was critical for the design team to understand because it
            meant that our design documentation also had to reflect the reality
            of how our components would be served.
          </motion.p>
          <motion.div variants={fadeInUp} className="w-full" data-id="element-510">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full" data-id="element-511">
              <div className="w-full bg-[#E8E4DF] rounded-2xl overflow-hidden" data-id="element-512">
                <img src="https://cdn.magicpatterns.com/uploads/m2St8up38smvhz7j1kEc22/ECiJiYsrUPWbDy5UNT5wz94igoo.png" alt="Helios DS Request page" className="w-full h-auto block" data-id="element-513" />
              </div>
              <div className="w-full bg-[#E8E4DF] rounded-2xl overflow-hidden relative" data-id="element-514">
                <img src="https://cdn.magicpatterns.com/uploads/m2St8up38smvhz7j1kEc22/ECiJiYsrUPWbDy5UNT5wz94igoo.png" alt="" className="w-full h-auto block opacity-0 pointer-events-none select-none" aria-hidden="true" data-id="element-515" />
                <img src="https://cdn.magicpatterns.com/uploads/oR5LsWFCyp7Apo9snqUwVH/component_library_screenshot.png" alt="Helios DS UI Components" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-516" />
              </div>
            </div>
            <p className="mt-4 text-sm text-[#A09A94] text-center leading-relaxed px-4" data-id="element-517">
              An intake system that I built within Coda to help us stay
              organized and help others not only learn about the components, but
              contribute to our system.
            </p>
          </motion.div>
        </motion.section>

        {/* Brand Theming */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-518">
          <motion.h2 id="consistency" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-519">
            Brand Theming
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl" data-id="element-520">
            Each brand could inject its distinct style and brand while "behind
            the scenes", components were all shared. If design needed a change
            to a stylistic property, one push to Gitlab would be propagated
            everywhere without needing tech support.
          </motion.p>
          <motion.div variants={fadeInUp} className="w-full" data-id="element-521">
            <div className="w-full rounded-2xl overflow-hidden" data-id="element-522">
              <img src="https://cdn.magicpatterns.com/uploads/fJt1ts3dNDnVCr1vaNaj8n/different_helios_pages.png" alt="Category landing pages across grocery, loyalty and beauty brands" className="w-full h-auto block" data-id="element-523" />
            </div>
            <p className="mt-3 text-xs text-[#A09A94] text-center leading-relaxed" data-id="element-524">
              Some examples of category landing pages for our grocery, loyalty
              and beauty brands.
            </p>
          </motion.div>
        </motion.section>

        {/* Measuring Success */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-525">
          <motion.h2 id="measuring-success" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-526">
            Measuring Success
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-527">
            {[{
            value: '1200+',
            label: 'Components Built'
          }, {
            value: '14',
            label: 'Banners Live'
          }, {
            value: '1 to 60+',
            label: 'Teams That Use the System'
          }].map((metric, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-6 rounded-2xl text-center" data-id="element-528">
                <span className="text-5xl font-bold text-[#1A1A1A] mb-4 block" data-id="element-529">
                  {metric.value}
                </span>
                <span className="text-sm text-[#A09A94] uppercase tracking-wider" data-id="element-530">
                  {metric.label}
                </span>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Key Learnings */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-531">
          <motion.h2 id="key-learnings" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-532">
            Key Learnings
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-533">
            {[{
            title: 'Tokens are the Superpower',
            desc: 'Investing in a robust token architecture early was the key to unlocking multi-brand support without code duplication.'
          }, {
            title: 'Governance is Cultural',
            desc: 'Building the system was the easy part. Changing how teams work together and contribute back was the real challenge.'
          }, {
            title: 'Meet Teams Where They Are',
            desc: 'We built tools to integrate Helios into existing workflows, rather than forcing teams to change their entire process.'
          }, {
            title: 'Documentation is a Product',
            desc: 'Great code is useless without great documentation. We treated our docs site as a primary product.'
          }].map((learning, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl" data-id="element-534">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-535">
                  {learning.title}
                </h3>
                <p className="text-sm leading-relaxed" data-id="element-536">{learning.desc}</p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Bottom Project Navigation */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="border-t border-[#E8E4DF] pt-12 flex justify-between items-center" data-id="element-537">
          <button onClick={() => {
          onBack();
          setTimeout(() => {
            document.getElementById('work')?.scrollIntoView({
              behavior: 'smooth'
            });
          }, 100);
        }} className="text-[#1A1A1A] hover:text-[#6B6560] transition-colors text-sm font-medium" data-id="element-538">
            ← Back to all work
          </button>
          <button onClick={onNext} className="group text-sm font-medium text-[#1A1A1A] hover:text-[#6B6560] transition-all duration-200 hover:translate-x-1" data-id="element-539">
            Next Project: {nextLabel} →
          </button>
        </motion.div>
      </div>

      {/* Fixed Section Navigator */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="fixed bottom-20 md:bottom-8 left-0 right-0 md:left-16 lg:left-56 flex items-center justify-center gap-2 z-40 px-4" data-id="element-540">
        <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-[#E8E4DF] rounded-full px-2 py-2 shadow-lg shadow-black/5" data-id="element-541">
          <button onClick={() => scrollTo(activeIndex - 1)} disabled={activeIndex === 0} className="w-10 h-10 rounded-full border border-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#F0EDEA] disabled:opacity-30 disabled:hover:bg-transparent transition-colors shrink-0" aria-label="Previous section" data-id="element-542">
            <ChevronLeft size={18} data-id="element-543" />
          </button>

          <AnimatePresence mode="wait" data-id="element-544">
            <motion.span key={activeIndex} initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.15
          }} className="text-sm font-medium text-[#1A1A1A] w-[160px] text-center px-2 select-none" data-id="element-545">
              {sections[activeIndex].label}
            </motion.span>
          </AnimatePresence>

          <button onClick={() => scrollTo(activeIndex + 1)} disabled={activeIndex === sections.length - 1} className="w-10 h-10 rounded-full border border-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#F0EDEA] disabled:opacity-30 disabled:hover:bg-transparent transition-colors shrink-0" aria-label="Next section" data-id="element-546">
            <ChevronRight size={18} data-id="element-547" />
          </button>
        </div>

        {/* Scroll progress pill — outside the chapter navigator */}
        <motion.span key={scrollProgress} initial={{
        opacity: 0.8
      }} animate={{
        opacity: 1
      }} className="bg-[#1A1A1A] text-white text-xs font-semibold tabular-nums rounded-full px-3 select-none shrink-0 w-[46px] text-center shadow-lg shadow-black/10 h-12 flex items-center justify-center" data-id="element-548">
          {scrollProgress}%
        </motion.span>
      </motion.div>
    </div>;
}